import { useEffect, useMemo, useState } from "react";
import { useGlobalFilter, usePagination, useSortBy, useTable } from "react-table";
import Helpers from "../../commons/helpers";
import { IActionMenuItem, IActionTypeSelectBox, ITableGroup } from "../../commons/interfaces";
import { Constants } from "../../constants/constants";
import { Mode } from "../../constants/enums";
import { DataLoading, DataNotFound } from "./Fragments";
import { PopupMenu, TableContainer, TableFilter, TableFooter, TableSearch } from "./Table";

interface ITableColumn<T> {
    key?: string;
    Header: string;
    noPad?: boolean;
    accessor: string;
    isSorted?: boolean;
    isSortedDesc?: boolean;
    width?: string | number;
    Cell?: (row: { value: any; row: { original: T; [key: string]: any } }) => JSX.Element;
    align?: "left" | "right" | "center";
}

// Declaring props types for DataTable
export interface DataTableProps<T> {
    loading?: boolean;
    mode?: number;
    isLocal?: boolean;
    isSorted?: boolean;
    showFooter?: boolean;
    isShowIndex?: boolean;
    noEndBorder?: boolean;

    filterForm?: JSX.Element;
    leftElement?: JSX.Element;
    table: {
        columns: ITableColumn<T>[];
        rows: T[];
    };

    totalCount?: number;
    pageNumber?: number;
    rowPerPage?: number;
    rowPerPageOptions?: number[];
    onChangePageSize?: (page: any) => void;
    onChangePageNumber?: (page: any) => void;

    searchText?: string;
    placeholderSearch?: string;
    onSearch?: (value: any) => void;
    onSearchChange?: (value: any) => void;

    onReset?: () => void;
    onFilter?: () => void;
    onCloseFilter?: () => void;

    allowCheckbox?: boolean;
    multiSelected?: boolean;
    disabledCheckdbox?: boolean;
    dataSelected?: string[];
    isRowSelectable?: (params: T) => boolean;
    handleOnChangeSelected?: (data: string[]) => void;

    actionTypeSelectBox?: (IActionTypeSelectBox | boolean)[];
    onSubmitActionCheckedBox?: (data: string[], actionType: number) => void;

    hideActionMenu?: boolean;
    actionList?: (IActionMenuItem | boolean)[] | ((row: T) => (IActionMenuItem | boolean)[]);

    pagination?: {
        variant: "contained" | "gradient";
        color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark" | "light";
    };

    tableGroup?: ITableGroup<T>[]
}

let defaultValue = 0;
let timeout: any;

export const DataTable = <T,>({
    mode,
    isLocal,
    isSorted,
    showFooter,
    noEndBorder,
    isShowIndex,

    table,
    filterForm,
    leftElement,

    totalCount,
    pageNumber,
    rowPerPage,
    rowPerPageOptions,
    onChangePageSize,
    onChangePageNumber,

    searchText,
    placeholderSearch,
    onSearch,
    onSearchChange,
    onReset,
    onFilter,
    onCloseFilter,

    allowCheckbox,
    multiSelected,
    disabledCheckdbox,
    dataSelected,
    isRowSelectable,
    handleOnChangeSelected,

    actionTypeSelectBox,
    onSubmitActionCheckedBox,

    actionList,
    hideActionMenu,

    pagination,
    loading,
    tableGroup = []
}: DataTableProps<T>): JSX.Element => {
    defaultValue = rowPerPage || Constants.ROW_PER_PAGE;

    const dataRowsByTable = useMemo<any>(() => table.rows || [], [table]);
    const dataColumnsByTable = useMemo<any>(() => table.columns, [table]);

    let dataPageOptions: number[] = [];
    for (let index = 0; index < Math.ceil(totalCount / defaultValue); index++) {
        dataPageOptions.push(index);
    }

    const tableInstance = useTable(
        {
            data: dataRowsByTable,
            columns: dataColumnsByTable,
            initialState: { pageIndex: pageNumber - 1 > 0 ? pageNumber - 1 : 0 },
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
        page,
        // pageOptions,
        canPreviousPage,
        // canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        setGlobalFilter,

        state: { pageIndex, pageSize, globalFilter },
    } = tableInstance;

    useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

    // Filter value state
    const [filter, setFilter] = useState(false);
    const [search, setSearch] = useState(searchText);

    useEffect(() => {
        setSearch(searchText);
    }, [searchText]);

    // Menu action value state
    const [openMenu, setOpenMenu] = useState<any>(false);
    const [currentItem, setCurrentItem] = useState<any>({});

    const [fetching, setFetching] = useState<boolean>(true);

    useEffect(() => {
        if (timeout) clearTimeout(timeout);
        if (loading === undefined && rows?.length === 0) timeout = setTimeout(() => setFetching(false), 1000);
        if (loading === undefined && rows?.length > 0) timeout = setTimeout(() => setFetching(false), 1000);
        if (loading !== undefined) setFetching(loading);
    }, [loading, rows]);

    return (
        <>
            {/* Header */}
            {(!Helpers.isNullOrEmpty(leftElement) ||
                Helpers.isFunction(onSearch) ||
                Helpers.isFunction(onSearchChange) ||
                Helpers.isFunction(onFilter)) && (
                <TableSearch
                    {...{
                        isLocal,
                        leftElement,
                        searchText,
                        placeholderSearch,
                        onSearch,
                        onSearchChange,
                        onFilter,
                        allowCheckbox,
                        dataSelected,
                        handleOnChangeSelected,
                        actionTypeSelectBox,
                        onSubmitActionCheckedBox,
                        setGlobalFilter,
                        search,
                        setFilter,
                        setSearch,
                    }}
                />
            )}
            {/* Content Table */}
            {fetching === true && <DataLoading />}
            {!fetching && Helpers.isNullOrEmpty(table) && <DataNotFound />}
            {!fetching && rows?.length === 0 && <DataNotFound />}
            {!fetching && rows?.length > 0 && (
                <TableContainer
                    {...{
                        mode,
                        isLocal,
                        isSorted,
                        noEndBorder,
                        isShowIndex,
                        table,
                        pageNumber,
                        allowCheckbox,
                        multiSelected,
                        disabledCheckdbox,
                        dataSelected,
                        isRowSelectable,
                        handleOnChangeSelected,
                        actionList,
                        hideActionMenu,

                        getTableProps,
                        getTableBodyProps,
                        headerGroups,
                        prepareRow,
                        rows,
                        page,
                        pageSize,
                        setCurrentItem,
                        setOpenMenu,
                        tableGroup
                    }}
                />
            )}

            {/* Footer */}
            {showFooter && (
                <TableFooter
                    {...{
                        defaultValue,
                        totalCount,
                        pageNumber,
                        rowPerPage,
                        rowPerPageOptions,
                        onChangePageSize,
                        onChangePageNumber,
                        pagination,
                        canPreviousPage,
                        gotoPage,
                        nextPage,
                        previousPage,
                        setPageSize,
                        pageIndex,
                        pageSize,
                    }}
                />
            )}
            <PopupMenu {...{ currentItem, openMenu, setOpenMenu, actionList }} />
            {/* Modal Filter */}
            {Helpers.isFunction(onFilter) && filter && (
                <TableFilter {...{ filterForm, onCloseFilter, onFilter, onReset, filter, setFilter, setSearch }} />
            )}
        </>
    );
};

// Declaring default props for DataTable
DataTable.defaultProps = {
    totalCount: 0,
    pageNumber: 1,
    rowPerPage: Constants.ROW_PER_PAGE,
    isSorted: false,
    showFooter: true,
    noEndBorder: false,
    multiSelected: true,
    hideActionMenu: false,
    mode: Mode.Create,
    pagination: { variant: "gradient", color: "info" },
    entriesPerPage: {
        defaultValue: Constants.ROW_PER_PAGE,
        entries: Constants.ROW_PER_PAGE_OPTIONS,
    },
};

export default DataTable;
